import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, Home as HomeIcon, X } from "lucide-react";
import { api } from "../../lib/api";
import { useSeo } from "../../lib/useSeo";
import { KoopCard } from "../../components/koop/KoopCard";
import { HuurKoopSwitch } from "../../components/koop/HuurKoopSwitch";
import { PropertySearchBar } from "../../components/search/PropertySearchBar";
import { ListingCardSkeleton } from "../../components/ListingCard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Slider } from "../../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../../components/ui/sheet";
import LeadCta from "../../components/leads/LeadCta";

const TYPES = [
  { value: "all", label: "Alle types" },
  { value: "huis", label: "Huis" },
  { value: "appartement", label: "Appartement" },
  { value: "studio", label: "Studio" },
  { value: "penthouse", label: "Penthouse" },
  { value: "villa", label: "Villa" },
];
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const FilterPanel = ({ f, setF, onApply, onReset, lockType }) => {
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="space-y-6">
      <div>
        <Label className="text-sm font-semibold text-navy">Plaats</Label>
        <Input data-testid="koop-filter-city" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Rotterdam" className="mt-2 rounded-xl" />
      </div>
      {!lockType && (
        <div>
          <Label className="text-sm font-semibold text-navy">Type</Label>
          <Select value={f.property_type || "all"} onValueChange={(v) => set("property_type", v === "all" ? "" : v)}>
            <SelectTrigger className="mt-2 rounded-xl" data-testid="koop-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-navy">Max. koopprijs</Label>
          <span className="text-sm font-bold text-brandblue">€{f.max_price.toLocaleString("nl-NL")}</span>
        </div>
        <Slider className="mt-3" min={100000} max={1500000} step={25000} value={[f.max_price]} onValueChange={(v) => set("max_price", v[0])} data-testid="koop-filter-price" />
      </div>
      <div>
        <Label className="text-sm font-semibold text-navy">Slaapkamers</Label>
        <div className="mt-2 flex gap-2">
          {[0, 1, 2, 3, 4].map((n) => (
            <button key={n} onClick={() => set("bedrooms", n)} className={`h-10 flex-1 rounded-xl border text-sm font-semibold ${f.bedrooms === n ? "border-brandblue bg-brandblue-light text-brandblue" : "border-border text-slate-600"}`}>{n === 0 ? "Alle" : `${n}+`}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-navy">Min. woonopp.</Label>
          <span className="text-sm font-bold text-brandblue">{f.min_m2} m²</span>
        </div>
        <Slider className="mt-3" min={0} max={300} step={10} value={[f.min_m2]} onValueChange={(v) => set("min_m2", v[0])} />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-navy">Min. perceel</Label>
          <span className="text-sm font-bold text-brandblue">{f.min_plot_m2} m²</span>
        </div>
        <Slider className="mt-3" min={0} max={1000} step={50} value={[f.min_plot_m2]} onValueChange={(v) => set("min_plot_m2", v[0])} />
      </div>
      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onReset} className="flex-1 rounded-xl" data-testid="koop-filter-reset">Wissen</Button>
        <Button onClick={onApply} className="flex-1 rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="koop-filter-apply">Toon woningen</Button>
      </div>
    </div>
  );
};

const Koopwoningen = ({ variant = "default", lockedType = "" }) => {
  const params = useParams();
  const cityParam = params.stad ? cap(params.stad.replace(/-/g, " ")) : "";
  const [sp, setSp] = useSearchParams();
  const empty = { city: cityParam, property_type: lockedType, max_price: 1500000, bedrooms: 0, min_m2: 0, min_plot_m2: 0 };
  const fromUrl = () => ({
    ...empty,
    city: cityParam || sp.get("city") || "",
    property_type: lockedType || sp.get("property_type") || "",
    max_price: sp.get("max_price") ? Number(sp.get("max_price")) : 1500000,
    min_m2: sp.get("min_m2") ? Number(sp.get("min_m2")) : 0,
    bedrooms: sp.get("bedrooms") ? Number(sp.get("bedrooms")) : 0,
  });
  const [f, setF] = useState(fromUrl);
  const [applied, setApplied] = useState(fromUrl);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sort = sp.get("sort") || "newest";
  const [seoMeta, setSeoMeta] = useState(null);

  const h1 = seoMeta?.h1 || (lockedType ? `${cap(lockedType)} kopen${cityParam ? ` in ${cityParam}` : ""}` : cityParam ? `Koopwoningen in ${cityParam}` : "Koopwoningen te koop");
  useSeo({
    title: seoMeta?.title || `${h1} — WoningAlert`,
    description: seoMeta?.description || `Bekijk het actuele koopaanbod${cityParam ? ` in ${cityParam}` : " in Nederland"}. Plan direct een bezichtiging via WoningAlert.`,
    robots: seoMeta?.robots,
    image: "/og-image.png",
  });

  const PAGE = 48;
  const buildQuery = (fil, s, offset) => {
    const p = new URLSearchParams();
    if (fil.city) p.set("city", fil.city);
    if (fil.property_type) p.set("property_type", fil.property_type);
    if (fil.min_price) p.set("min_price", fil.min_price);
    if (fil.max_price && fil.max_price < 1500000) p.set("max_price", fil.max_price);
    if (fil.bedrooms) p.set("bedrooms", fil.bedrooms);
    if (fil.min_m2) p.set("min_m2", fil.min_m2);
    if (fil.min_plot_m2) p.set("min_plot_m2", fil.min_plot_m2);
    p.set("sort", s); p.set("limit", PAGE); p.set("offset", offset);
    return p.toString();
  };

  const fetchListings = useCallback((fil, s) => {
    setData(null); setError(false);
    api(`/koop/listings?${buildQuery(fil, s, 0)}`).then(setData).catch(() => setError(true));
  }, []);

  // Sync from URL (path + search params) so the compact search bar drives the results.
  const spString = sp.toString();
  useEffect(() => {
    const next = fromUrl();
    // include min_price from URL
    next.min_price = sp.get("min_price") ? Number(sp.get("min_price")) : 0;
    setF(next); setApplied(next); fetchListings(next, sort);
  }, [cityParam, lockedType, spString]);

  // SEO meta for city/type variants (robots + h1 from backend)
  useEffect(() => {
    if (variant === "city" && cityParam) {
      api(`/seo/koop/category/koopwoningen/${params.stad}`).then((d) => setSeoMeta(d.seo)).catch(() => {});
    } else if (variant === "type" && cityParam && lockedType) {
      api(`/seo/koop/category/${lockedType}-kopen/${params.stad}`).then((d) => setSeoMeta(d.seo)).catch(() => {});
    }
  }, [variant, cityParam, lockedType, params.stad]);

  const apply = () => { setApplied(f); fetchListings(f, sort); setSheetOpen(false); };
  const reset = () => { setF(empty); setApplied(empty); fetchListings(empty, sort); setSheetOpen(false); };
  const changeSort = (s) => { const n = new URLSearchParams(sp); n.set("sort", s); setSp(n); fetchListings(applied, s); };
  const loadMore = () => {
    if (!data || loadingMore) return;
    setLoadingMore(true);
    api(`/koop/listings?${buildQuery(applied, sort, data.listings.length)}`)
      .then((d) => setData((prev) => ({ ...d, listings: [...(prev?.listings || []), ...d.listings] })))
      .catch(() => {}).finally(() => setLoadingMore(false));
  };

  const listings = data?.listings || [];

  return (
    <div>
      <div className="relative overflow-hidden bg-navy">
        <img
          src="https://customer-assets-jt897jd0.emergentagent.net/job_ad0cee43-adb9-42e2-b070-e52d3c5da298/artifacts/gi8kydv6_Monochrome%20villa%20with%20infinitypool.webp"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-navy/85" />
        <div className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full bg-brandblue/30 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 py-10 sm:px-6">
          <HuurKoopSwitch active="koop" className="mb-6" />
          <h1 className="font-display text-3xl font-extrabold text-white sm:text-4xl" role="heading" aria-level="1">{h1}</h1>
          <p className="mt-1 text-sm text-white/70" data-testid="koop-count">{data ? `${data.total} koopwoning(en)` : "Laden…"}</p>
          {/* Compact Funda-style search bar */}
          <div className="mt-6">
            <PropertySearchBar
              variant="compact"
              defaultMode="koop"
              initial={{
                city: applied.city,
                min_price: sp.get("min_price") || undefined,
                max_price: applied.max_price < 1500000 ? applied.max_price : undefined,
                property_type: applied.property_type,
              }}
            />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-slate-400">{applied.city ? `Plaats: ${applied.city}` : "Heel Nederland"}</div>
          <div className="flex items-center gap-2">
            <Select value={sort} onValueChange={changeSort}>
              <SelectTrigger className="w-[180px] rounded-xl" data-testid="koop-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Nieuwste eerst</SelectItem>
                <SelectItem value="price_asc">Prijs laag → hoog</SelectItem>
                <SelectItem value="price_desc">Prijs hoog → laag</SelectItem>
                <SelectItem value="match_desc">Beste match</SelectItem>
                <SelectItem value="size_desc">Grootste woning</SelectItem>
              </SelectContent>
            </Select>
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="outline" className="rounded-xl" data-testid="koop-open-filters"><SlidersHorizontal size={16} className="mr-2" /> Filters</Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[340px] overflow-y-auto">
                <SheetHeader><SheetTitle className="font-display">Filters</SheetTitle></SheetHeader>
                <div className="mt-6"><FilterPanel f={f} setF={setF} onApply={apply} onReset={reset} lockType={!!lockedType} /></div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[280px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-3xl border border-border bg-white p-6 shadow-sm">
              <h2 className="mb-5 flex items-center gap-2 font-display text-lg font-bold text-navy"><SlidersHorizontal size={18} /> Filters</h2>
              <FilterPanel f={f} setF={setF} onApply={apply} onReset={reset} lockType={!!lockedType} />
            </div>
          </aside>
          <section>
            {error ? (
              <div className="rounded-3xl border border-border p-12 text-center">
                <p className="text-slate-500">Er ging iets mis.</p>
                <Button onClick={() => fetchListings(applied, sort)} className="mt-4 rounded-xl">Opnieuw</Button>
              </div>
            ) : data === null ? (
              <div className="grid gap-5 sm:grid-cols-2">{Array.from({ length: 6 }).map((_, i) => <ListingCardSkeleton key={i} />)}</div>
            ) : listings.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-border p-16 text-center" data-testid="koop-empty">
                <HomeIcon className="mx-auto text-slate-300" size={44} />
                <p className="mt-4 text-slate-500">Geen koopwoningen gevonden. Pas je filters aan of stel een koopalert in.</p>
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2" data-testid="koop-grid">
                {listings.map((l) => <KoopCard key={l.id} listing={l} />)}
              </div>
            )}
            {data?.has_more && listings.length > 0 && (
              <div className="mt-10 flex justify-center">
                <Button onClick={loadMore} disabled={loadingMore} variant="outline" className="rounded-xl px-8" data-testid="koop-load-more">
                  {loadingMore ? "Laden…" : `Meer laden (${listings.length}/${data.total})`}
                </Button>
              </div>
            )}
          </section>
        </div>

        {/* Lead CTAs */}
        <div className="mt-14 grid gap-6 lg:grid-cols-2" data-testid="koop-lead-ctas">
          <LeadCta type="mortgage" />
          <LeadCta type="sale" />
        </div>
      </div>
    </div>
  );
};

export default Koopwoningen;
